import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import { FileEdit, ArrowRight, Bug, Lightbulb, Mail, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import PortalBottomNav from '../components/PortalBottomNav';
import { toast } from 'sonner';

const PRESET_ICONS = { bug: Bug, feature: Lightbulb, contact: Mail };

const PublicForms = () => {
  const { subdomain, slug } = useParams();
  const [searchParams] = useSearchParams();
  const accessKey = searchParams.get('key') || '';
  const [workspace, setWorkspace] = useState(null);
  const [forms, setForms] = useState([]);
  const [activeForm, setActiveForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, [subdomain, slug]);

  const load = async () => {
    setLoading(true);
    try {
      const keyParam = accessKey ? `?key=${accessKey}` : '';
      const [ws, fs] = await Promise.all([
        axios.get(`${API_URL}/public/workspaces/${subdomain}${keyParam}`),
        axios.get(`${API_URL}/public/workspaces/${subdomain}/forms`),
      ]);
      setWorkspace(ws.data);
      setForms(fs.data.forms || []);
      if (slug) {
        const found = (fs.data.forms || []).find(f => f.slug === slug);
        if (found) setActiveForm(found);
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/public/workspaces/${subdomain}/forms/${activeForm.slug}/submit`, {
        data: formData,
      });
      setSubmitted(true);
      toast.success(res.data.message || 'Submitted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = workspace?.settings?.primary_color || '#7c3aed';

  return (
    <div className="min-h-screen bg-zinc-50 pb-24" data-testid="public-forms-page">
      <div style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}>
        <div className="max-w-3xl mx-auto px-6 py-12 text-white">
          <div className="flex items-center gap-3 mb-2">
            <FileEdit className="h-6 w-6 opacity-80" />
            <span className="text-sm font-medium opacity-90">{workspace?.name}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
            {activeForm?.title || 'How can we help?'}
          </h1>
          <p className="mt-2 text-white/80">
            {activeForm?.description || 'Pick a form to get started.'}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {loading ? <p className="text-center py-8 text-zinc-500">Loading…</p> : activeForm ? (
          submitted ? (
            <div className="bg-white rounded-2xl border border-zinc-200 p-10 text-center" data-testid="form-submitted">
              <div className="h-14 w-14 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                <Check className="h-7 w-7" style={{ color: primaryColor }} />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900 mt-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{activeForm.success_message || 'Thank you!'}</h2>
              <p className="text-sm text-zinc-500 mt-2">We'll get back to you soon.</p>
              <Button
                className="mt-6"
                onClick={() => { setActiveForm(null); setFormData({}); setSubmitted(false); }}
                style={{ backgroundColor: primaryColor }}
              >
                Submit another
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-zinc-200 p-6">
              <button onClick={() => { setActiveForm(null); setFormData({}); }} className="text-sm text-zinc-500 hover:text-zinc-900 mb-4">← All forms</button>
              <form onSubmit={handleSubmit} className="space-y-4">
                {(activeForm.fields || []).map(field => (
                  <div key={field.id}>
                    <Label className="text-sm font-medium">
                      {field.label}{field.required && <span className="text-red-500">*</span>}
                    </Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        value={formData[field.label] || ''}
                        onChange={e => setFormData({...formData, [field.label]: e.target.value})}
                        placeholder={field.placeholder || ''}
                        required={field.required}
                        rows={4}
                        className="mt-1.5"
                        data-testid={`field-${field.id}`}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        value={formData[field.label] || ''}
                        onChange={e => setFormData({...formData, [field.label]: e.target.value})}
                        required={field.required}
                        className="mt-1.5 w-full h-10 px-3 rounded-md border border-zinc-200 bg-white text-sm"
                        data-testid={`field-${field.id}`}
                      >
                        <option value="">Select…</option>
                        {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : field.type === 'checkbox' ? (
                      <div className="mt-1.5 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData[field.label] === 'true'}
                          onChange={e => setFormData({...formData, [field.label]: e.target.checked ? 'true' : ''})}
                          className="rounded"
                          data-testid={`field-${field.id}`}
                        />
                        <span className="text-sm">{field.placeholder || field.label}</span>
                      </div>
                    ) : (
                      <Input
                        type={field.type}
                        value={formData[field.label] || ''}
                        onChange={e => setFormData({...formData, [field.label]: e.target.value})}
                        placeholder={field.placeholder || ''}
                        required={field.required}
                        className="mt-1.5"
                        data-testid={`field-${field.id}`}
                      />
                    )}
                    {field.help_text && <p className="text-xs text-zinc-500 mt-1">{field.help_text}</p>}
                  </div>
                ))}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 text-white font-semibold"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="form-submit-button"
                >
                  {submitting ? 'Submitting…' : (activeForm.submit_button_text || 'Submit')}
                </Button>
              </form>
            </div>
          )
        ) : forms.length === 0 ? (
          <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center">
            <FileEdit className="h-12 w-12 mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500">No forms available yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {forms.map(f => {
              const Icon = PRESET_ICONS[f.preset] || FileEdit;
              return (
                <button
                  key={f.id}
                  onClick={() => setActiveForm(f)}
                  className="bg-white border border-zinc-200 rounded-2xl p-5 hover:border-zinc-300 hover:shadow-sm transition-all text-left group"
                  data-testid={`public-form-${f.slug}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                      <Icon className="h-5 w-5" style={{ color: primaryColor }} />
                    </div>
                    <ArrowRight className="h-4 w-4 text-zinc-300 group-hover:text-zinc-600 transition-colors" />
                  </div>
                  <h3 className="font-bold text-zinc-900">{f.title}</h3>
                  {f.description && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{f.description}</p>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <PortalBottomNav workspace={workspace} accessKey={accessKey} />
    </div>
  );
};

export default PublicForms;
